import {AfterViewInit, Component, Injector, OnDestroy, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import {
    AbstractStepTransactionComponent,
    EdocEvent,
    EdocEventType,
    EdocOptions,
    NotificationType,
    PesFormatNamingContextRegistry,
    PesModalService,
    PesStyle,
    PesTask,
    PesTaskEventType,
    PesTaskService,
    ScrollService,
    Signatory,
    SignatureEdocStep,
    SignUrlEvent,
    SubscriptionManager
} from '@pes/ui';
import {ISubscription} from 'rxjs/Subscription';
import {finalize, mergeMap} from 'rxjs/operators';
import {WelcomeDialogModel} from '../dialog/welcome-dialog/welcome-dialog.model';
import {WelcomeDialogComponent} from '../dialog/welcome-dialog/welcome-dialog.component';
import {WelcomeDialogModelBuilder} from '../dialog/welcome-dialog/welcome-dialog-model.builder';
import {TagCommanderService} from '../../utils/service/tag-commander.service';


/**
 * The view loaded into a circuit for signing documents.
 */
@Component({
    selector: 'action-signature-edoc',
    templateUrl: './step-signature-edoc.component.html',
})
export class StepSignatureEdocComponent extends AbstractStepTransactionComponent implements OnInit, OnDestroy, AfterViewInit {
    /**
     * The reference to the view name.
     */
    public static readonly VIEW_REF: string = 'StepSignatureEdocComponent';
    /**
     * The title of the step.
     */
    public title: string = null;
    /**
     * The reference to the step associated with this step view.
     */
    public step: SignatureEdocStep = null;
    /**
     * @inheritDoc
     */
    @ViewChild('container', {read: ViewContainerRef}) protected container: ViewContainerRef;

    /**
     * The styles for all PES cards in this view.
     */
    protected cardStyle: PesStyle = null;

    /**
     * The reference to the PES task service.
     */
    protected taskService: PesTaskService = null;

    /**
     * Reference to the PES ScrollService service.
     */
    private readonly _scrollService: ScrollService = null;

    private readonly _modal: PesModalService = null;

    private signEventReceived: boolean = false;

    private eventForScrollReceived: boolean = false;

    private readonly tagCommanderService: TagCommanderService = null;

    constructor(protected injector: Injector) {
        super(injector, StepSignatureEdocComponent.VIEW_REF);
        this.taskService = injector.get(PesTaskService);
        this._scrollService = injector.get(ScrollService);
        this._modal = injector.get(PesModalService);
        this.tagCommanderService = this.injector.get(TagCommanderService);
        SubscriptionManager.getInstance().createSubscribable(this);
    }

    public ngOnInit(): void {
        // set page name for analytics
        this.tagCommanderService.tagPageSign(this.task.data.context.contractType);
        this.stepTransactionManager = this.stepTransactionManagerFactory.create(this.step);
        this.refresh();
    }

    public ngOnDestroy(): void {
        SubscriptionManager.getInstance().deleteSubscribable(this);
    }

    ngAfterViewInit(): void {
        const model: WelcomeDialogModel = WelcomeDialogModelBuilder.build(
            'lbpa.modals.welcome.title',
            '',
            NotificationType.PRIMARY,
            'lbpa.modals.welcome.submit'
        );
        const sub: ISubscription = this._modal.dialog(WelcomeDialogComponent, model, PesModalService.GLOBAL_OPTIONS).subscribe((response: any) => {
        });
        SubscriptionManager.getInstance().registerSubscription(this, sub);
    }


    /**
     * Updates step data.
     */
    public refresh(): void {
        this.orchestrator.forceLoading(true);
        const taskRef: string = this.task.reference;
        const edocOptions: EdocOptions[] = this.step.edocOptions;

        const currentSignatory = this.task.data.taskData.signatories.find((signatory: Signatory) => signatory.user.id === this.task.assignee);

        const sub: ISubscription = this.taskService.sendTaskEvent(
            taskRef,
            SignUrlEvent.create(PesTaskEventType.REFRESH_SIGN, currentSignatory.signatoryId, edocOptions)
        ).pipe(
            finalize(() => this.orchestrator.forceLoading(false)),
            mergeMap(() => this.taskService.refresh(taskRef))
        ).subscribe((task: PesTask) => {
            this.task = task;
            this.stepTransactionManager.update(task);
            this.orchestrator.forceLoading(false);
        });
        SubscriptionManager.getInstance().registerSubscription(this, sub);
    }

    /**
     * Invoked each time the edoc component list dispatches an edoc event.
     *
     * @param {EdocEvent} event the event responsible for the method invocation.
     */
    protected onEdocEventHandler(event: EdocEvent): void {
        if (event.type === EdocEventType.EDOC_DONE) {
            if (!this.signEventReceived && event.data.action === 'OK') {
                this.tagCommanderService.click();
                this.signEventReceived = true;
                this.orchestrator.forceLoading(true);
                this.orchestrator.checkValidAndSubmit({
                    stepIdx: this.task.data.stepIdx,
                    taskData: this.task.data.taskData
                }).pipe(
                    finalize(() => {
                        this.orchestrator.forceLoading(false)
                    })
                ).subscribe(() => {
                    this.stepEventService.doExitStepEvent(this);
                    this.orchestrator.goToNext();
                });
            }
        } else if (event.type === EdocEventType.EDOC_DOCREAD) {
            this.eventForScrollReceived = false;
        } else if (event.type === EdocEventType.EDOC_PAGELOADED && event.data.docIndex !== 0 && !this.eventForScrollReceived) {
            this._scrollService.scrollTo();
            this.eventForScrollReceived = true;
        }
    }
}

PesFormatNamingContextRegistry.getInstance().delete(StepSignatureEdocComponent.VIEW_REF);
PesFormatNamingContextRegistry.getInstance().add(StepSignatureEdocComponent.VIEW_REF, StepSignatureEdocComponent);



// WEBPACK FOOTER //
// ./src/app/component/step-signature-edoc/step-signature-edoc.component.ts